import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { translateLocation } from '../../data/locationTranslations';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { isInChinaSync } from '../../utils/regionDetect';
import {
  Briefcase, Eye, EyeOff, ArrowRight, ArrowLeft, Check, X,
  User, Mail, Lock, MapPin, Phone, Building2, CreditCard, FileText, ChevronDown, ChevronUp,
  DollarSign, Shield, Clock, Search, Upload, Info
} from 'lucide-react';
import { L, kicker as luxeKicker, display, btn, LuxeStyles, useIsDesktop, DiamondMark } from '../../theme/luxe';

// Small/secondary text is never lighter than #6E685F (matches the approved artboard).
const MUTE = '#6E685F';
const kicker = (extra = {}) => luxeKicker({ color: MUTE, ...extra });

const MatchmakerRegister = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { language, setLanguage: switchLanguage } = useLanguage();
  const isDesktop = useIsDesktop();
  useEffect(() => {
    if (!sessionStorage.getItem('auth_lang_touched')) {
      sessionStorage.setItem('auth_lang_touched', '1');
      if (language !== 'zh') switchLanguage('zh');
    }
  }, []); // eslint-disable-line
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    phone: '',
    country: '',
    state: '',
    city: '',
    referral_code: searchParams.get('ref') || '',
    agreedToTerms: false,
    w9File: null,
    ssn: '',
  });
  const [w9Preview, setW9Preview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showSsn, setShowSsn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [direction, setDirection] = useState('next');
  const inputRef = useRef(null);

  // Email-availability gate. When the user advances past the email step we
  // call /public/auth/check-matchmaker-email. If the address belongs to a
  // member (profile role), we stash the profile here and force the user to
  // confirm it's them before progressing. Confirmed emails are remembered
  // so they don't get re-prompted on every Back/Next.
  const [emailCheck, setEmailCheck] = useState({ status: 'idle', loading: false, profile: null });
  const [confirmedMemberEmail, setConfirmedMemberEmail] = useState('');

  // Signature state
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  // Single default handwriting-style signature font (no style picker). Uses
  // system script fonts so it looks handwritten without loading a web font.
  const SIGNATURE_FONT = "'Snell Roundhand', 'Segoe Script', 'Brush Script MT', 'Bradley Hand', cursive";
  const [signatureStyle, setSignatureStyle] = useState(SIGNATURE_FONT);
  const [signatureData, setSignatureData] = useState(null);

  // Partner agreement content from admin settings
  const [partnerAgreement, setPartnerAgreement] = useState(null);

  // Determine matchmaker type
  const isExternal = searchParams.get('type') === 'external_matchmaker';

  // Plan selection state (external only)
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [plansLoading, setPlansLoading] = useState(false);

  // Payment instructions state (external only)
  const [paymentInstructions, setPaymentInstructions] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [expandedMethod, setExpandedMethod] = useState(null);

  // City search state
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const cityDropdownRef = useRef(null);

  useEffect(() => {
    if (isExternal) {
      setPlansLoading(true);
      api.get('/public/subscription-plans')
        .then(res => {
          const plans = res.data?.plans || [];
          setAvailablePlans(plans);
        })
        .catch(err => console.error('Failed to load plans:', err))
        .finally(() => setPlansLoading(false));

      setPaymentLoading(true);
      // Strip PayPal out for users detected as Mainland China — paypal.com
      // is throttled / unreliable from the GFW so the merchant-side flow
      // routinely stalls. Whitelist mirrors chinaRestrictions.js spec so
      // bank_transfer / zelle / check / alipay / wechat_pay still surface.
      const inChina = isInChinaSync();
      api.get('/public/payment-instructions')
        .then(res => {
          const all = res.data?.instructions || [];
          const filtered = inChina
            ? all.filter(m => String(m.payment_method || '').toLowerCase() !== 'paypal')
            : all;
          setPaymentInstructions(filtered);
        })
        .catch(err => console.error('Failed to load payment instructions:', err))
        .finally(() => setPaymentLoading(false));
    }
  }, [isExternal]);

  // Fetch partner agreement content
  useEffect(() => {
    api.get('/public/partner-agreement')
      .then(res => { if (res.data) setPartnerAgreement(res.data); })
      .catch(err => console.error('Failed to load partner agreement:', err));
  }, []);

  // Close city dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(e.target)) {
        setShowCityDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Location data - Countries, States, Cities (reused from ProfileForm.js)
  const locationData = {
    'United States': {
      'Alabama': ['Birmingham', 'Montgomery', 'Huntsville', 'Mobile', 'Tuscaloosa'],
      'Alaska': ['Anchorage', 'Fairbanks', 'Juneau'],
      'Arizona': ['Phoenix', 'Tucson', 'Mesa', 'Scottsdale', 'Chandler', 'Tempe'],
      'Arkansas': ['Little Rock', 'Fort Smith', 'Fayetteville'],
      'California': ['Los Angeles', 'San Francisco', 'San Diego', 'San Jose', 'Sacramento', 'Fresno', 'Oakland', 'Irvine', 'Pasadena', 'Long Beach', 'Santa Monica', 'Beverly Hills', 'Anaheim', 'Riverside'],
      'Colorado': ['Denver', 'Colorado Springs', 'Aurora', 'Boulder', 'Fort Collins'],
      'Connecticut': ['Hartford', 'New Haven', 'Stamford', 'Bridgeport'],
      'Delaware': ['Wilmington', 'Dover', 'Newark'],
      'Florida': ['Miami', 'Orlando', 'Tampa', 'Jacksonville', 'Fort Lauderdale', 'St. Petersburg', 'Naples', 'West Palm Beach'],
      'Georgia': ['Atlanta', 'Savannah', 'Augusta', 'Athens'],
      'Hawaii': ['Honolulu', 'Hilo', 'Kailua'],
      'Idaho': ['Boise', 'Meridian', 'Nampa'],
      'Illinois': ['Chicago', 'Aurora', 'Naperville', 'Springfield', 'Rockford'],
      'Indiana': ['Indianapolis', 'Fort Wayne', 'Evansville', 'South Bend'],
      'Iowa': ['Des Moines', 'Cedar Rapids', 'Iowa City'],
      'Kansas': ['Wichita', 'Overland Park', 'Kansas City', 'Topeka'],
      'Kentucky': ['Louisville', 'Lexington', 'Bowling Green'],
      'Louisiana': ['New Orleans', 'Baton Rouge', 'Shreveport'],
      'Maine': ['Portland', 'Bangor', 'Augusta'],
      'Maryland': ['Baltimore', 'Bethesda', 'Rockville', 'Annapolis'],
      'Massachusetts': ['Boston', 'Cambridge', 'Worcester', 'Springfield'],
      'Michigan': ['Detroit', 'Grand Rapids', 'Ann Arbor', 'Lansing'],
      'Minnesota': ['Minneapolis', 'St. Paul', 'Rochester', 'Duluth'],
      'Mississippi': ['Jackson', 'Gulfport', 'Biloxi'],
      'Missouri': ['Kansas City', 'St. Louis', 'Springfield', 'Columbia'],
      'Montana': ['Billings', 'Missoula', 'Great Falls'],
      'Nebraska': ['Omaha', 'Lincoln'],
      'Nevada': ['Las Vegas', 'Reno', 'Henderson'],
      'New Hampshire': ['Manchester', 'Nashua', 'Concord'],
      'New Jersey': ['Newark', 'Jersey City', 'Trenton', 'Princeton', 'Hoboken'],
      'New Mexico': ['Albuquerque', 'Santa Fe', 'Las Cruces'],
      'New York': ['New York City', 'Buffalo', 'Rochester', 'Albany', 'Syracuse', 'Brooklyn', 'Queens', 'Manhattan'],
      'North Carolina': ['Charlotte', 'Raleigh', 'Durham', 'Greensboro', 'Asheville'],
      'North Dakota': ['Fargo', 'Bismarck', 'Grand Forks'],
      'Ohio': ['Columbus', 'Cleveland', 'Cincinnati', 'Toledo', 'Akron'],
      'Oklahoma': ['Oklahoma City', 'Tulsa', 'Norman'],
      'Oregon': ['Portland', 'Salem', 'Eugene', 'Bend'],
      'Pennsylvania': ['Philadelphia', 'Pittsburgh', 'Harrisburg', 'Allentown'],
      'Rhode Island': ['Providence', 'Warwick', 'Newport'],
      'South Carolina': ['Charleston', 'Columbia', 'Greenville', 'Myrtle Beach'],
      'South Dakota': ['Sioux Falls', 'Rapid City'],
      'Tennessee': ['Nashville', 'Memphis', 'Knoxville', 'Chattanooga'],
      'Texas': ['Houston', 'Dallas', 'Austin', 'San Antonio', 'Fort Worth', 'El Paso', 'Plano', 'Frisco'],
      'Utah': ['Salt Lake City', 'Provo', 'Park City'],
      'Vermont': ['Burlington', 'Montpelier'],
      'Virginia': ['Virginia Beach', 'Richmond', 'Arlington', 'Alexandria', 'Norfolk'],
      'Washington': ['Seattle', 'Tacoma', 'Spokane', 'Bellevue', 'Olympia'],
      'West Virginia': ['Charleston', 'Huntington', 'Morgantown'],
      'Wisconsin': ['Milwaukee', 'Madison', 'Green Bay'],
      'Wyoming': ['Cheyenne', 'Casper', 'Jackson'],
      'Washington DC': ['Washington']
    },
    'Canada': {
      'Alberta': ['Calgary', 'Edmonton', 'Red Deer', 'Lethbridge'],
      'British Columbia': ['Vancouver', 'Victoria', 'Surrey', 'Burnaby', 'Kelowna'],
      'Manitoba': ['Winnipeg', 'Brandon'],
      'New Brunswick': ['Fredericton', 'Saint John', 'Moncton'],
      'Newfoundland and Labrador': ['St. John\'s', 'Corner Brook'],
      'Nova Scotia': ['Halifax', 'Sydney', 'Dartmouth'],
      'Ontario': ['Toronto', 'Ottawa', 'Mississauga', 'Hamilton', 'London', 'Brampton', 'Markham'],
      'Prince Edward Island': ['Charlottetown'],
      'Quebec': ['Montreal', 'Quebec City', 'Laval', 'Gatineau'],
      'Saskatchewan': ['Saskatoon', 'Regina']
    },
    'United Kingdom': {
      'England': ['London', 'Manchester', 'Birmingham', 'Liverpool', 'Leeds', 'Bristol', 'Oxford', 'Cambridge', 'Brighton', 'Bath'],
      'Scotland': ['Edinburgh', 'Glasgow', 'Aberdeen', 'Dundee'],
      'Wales': ['Cardiff', 'Swansea', 'Newport'],
      'Northern Ireland': ['Belfast', 'Derry', 'Lisburn']
    },
    'Australia': {
      'New South Wales': ['Sydney', 'Newcastle', 'Wollongong'],
      'Victoria': ['Melbourne', 'Geelong', 'Ballarat'],
      'Queensland': ['Brisbane', 'Gold Coast', 'Cairns', 'Townsville'],
      'Western Australia': ['Perth', 'Fremantle'],
      'South Australia': ['Adelaide'],
      'Tasmania': ['Hobart', 'Launceston'],
      'Australian Capital Territory': ['Canberra'],
      'Northern Territory': ['Darwin', 'Alice Springs']
    },
    'China': {
      'Beijing': ['Beijing'],
      'Shanghai': ['Shanghai'],
      'Guangdong': ['Guangzhou', 'Shenzhen', 'Dongguan', 'Foshan', 'Zhuhai', 'Zhongshan'],
      'Zhejiang': ['Hangzhou', 'Ningbo', 'Wenzhou', 'Jiaxing'],
      'Jiangsu': ['Nanjing', 'Suzhou', 'Wuxi', 'Changzhou'],
      'Sichuan': ['Chengdu', 'Mianyang', 'Deyang'],
      'Hubei': ['Wuhan', 'Yichang'],
      'Hunan': ['Changsha', 'Zhuzhou'],
      'Fujian': ['Fuzhou', 'Xiamen', 'Quanzhou'],
      'Shandong': ['Jinan', 'Qingdao', 'Yantai'],
      'Tianjin': ['Tianjin'],
      'Chongqing': ['Chongqing'],
      'Liaoning': ['Shenyang', 'Dalian'],
      'Shaanxi': ['Xi\'an'],
      'Henan': ['Zhengzhou', 'Luoyang']
    },
    'Taiwan': {
      'Taipei': ['Taipei', 'New Taipei'],
      'Taichung': ['Taichung'],
      'Kaohsiung': ['Kaohsiung'],
      'Tainan': ['Tainan'],
      'Taoyuan': ['Taoyuan']
    },
    'Japan': {
      'Tokyo': ['Tokyo', 'Shibuya', 'Shinjuku', 'Minato'],
      'Osaka': ['Osaka', 'Sakai'],
      'Kanagawa': ['Yokohama', 'Kawasaki'],
      'Aichi': ['Nagoya'],
      'Hokkaido': ['Sapporo'],
      'Kyoto': ['Kyoto'],
      'Fukuoka': ['Fukuoka']
    },
    'South Korea': {
      'Seoul': ['Seoul'],
      'Busan': ['Busan'],
      'Gyeonggi': ['Suwon', 'Incheon', 'Seongnam'],
      'Daegu': ['Daegu']
    },
    'Singapore': { 'Singapore': ['Singapore'] },
    'Hong Kong': { 'Hong Kong': ['Hong Kong'] },
    'Malaysia': {
      'Kuala Lumpur': ['Kuala Lumpur'],
      'Selangor': ['Petaling Jaya', 'Shah Alam', 'Subang Jaya'],
      'Penang': ['George Town'],
      'Johor': ['Johor Bahru']
    },
    'Germany': {
      'Bavaria': ['Munich', 'Nuremberg'],
      'Berlin': ['Berlin'],
      'Hamburg': ['Hamburg'],
      'North Rhine-Westphalia': ['Cologne', 'Düsseldorf', 'Dortmund'],
      'Hesse': ['Frankfurt', 'Wiesbaden'],
      'Baden-Württemberg': ['Stuttgart', 'Heidelberg']
    },
    'France': {
      'Île-de-France': ['Paris', 'Versailles'],
      'Provence-Alpes-Côte d\'Azur': ['Marseille', 'Nice', 'Cannes'],
      'Auvergne-Rhône-Alpes': ['Lyon', 'Grenoble'],
      'Nouvelle-Aquitaine': ['Bordeaux'],
      'Occitanie': ['Toulouse', 'Montpellier']
    },
    'Italy': {
      'Lazio': ['Rome'],
      'Lombardy': ['Milan'],
      'Veneto': ['Venice', 'Verona'],
      'Tuscany': ['Florence', 'Pisa'],
      'Campania': ['Naples']
    },
    'Spain': {
      'Community of Madrid': ['Madrid'],
      'Catalonia': ['Barcelona'],
      'Andalusia': ['Seville', 'Malaga', 'Granada'],
      'Valencia': ['Valencia']
    },
    'Netherlands': {
      'North Holland': ['Amsterdam'],
      'South Holland': ['Rotterdam', 'The Hague'],
      'Utrecht': ['Utrecht']
    },
    'Sweden': {
      'Stockholm': ['Stockholm'],
      'Västra Götaland': ['Gothenburg'],
      'Skåne': ['Malmö']
    },
    'Switzerland': {
      'Zurich': ['Zurich'],
      'Geneva': ['Geneva'],
      'Bern': ['Bern']
    },
    'Brazil': {
      'São Paulo': ['São Paulo', 'Campinas'],
      'Rio de Janeiro': ['Rio de Janeiro'],
      'Minas Gerais': ['Belo Horizonte'],
      'Bahia': ['Salvador']
    },
    'Mexico': {
      'Mexico City': ['Mexico City'],
      'Jalisco': ['Guadalajara'],
      'Nuevo León': ['Monterrey'],
      'Quintana Roo': ['Cancún']
    },
    'India': {
      'Maharashtra': ['Mumbai', 'Pune'],
      'Delhi': ['New Delhi'],
      'Karnataka': ['Bangalore', 'Mysore'],
      'Tamil Nadu': ['Chennai'],
      'Telangana': ['Hyderabad'],
      'West Bengal': ['Kolkata']
    },
    'Thailand': {
      'Bangkok': ['Bangkok'],
      'Chiang Mai': ['Chiang Mai'],
      'Phuket': ['Phuket']
    },
    'Philippines': {
      'Metro Manila': ['Manila', 'Quezon City', 'Makati'],
      'Cebu': ['Cebu City']
    },
    'Indonesia': {
      'Jakarta': ['Jakarta'],
      'Bali': ['Denpasar', 'Ubud'],
      'East Java': ['Surabaya']
    },
    'Vietnam': {
      'Ho Chi Minh City': ['Ho Chi Minh City'],
      'Hanoi': ['Hanoi'],
      'Da Nang': ['Da Nang']
    },
    'New Zealand': {
      'Auckland': ['Auckland'],
      'Wellington': ['Wellington'],
      'Canterbury': ['Christchurch']
    },
    'United Arab Emirates': {
      'Dubai': ['Dubai'],
      'Abu Dhabi': ['Abu Dhabi'],
      'Sharjah': ['Sharjah']
    },
    'South Africa': {
      'Gauteng': ['Johannesburg', 'Pretoria'],
      'Western Cape': ['Cape Town'],
      'KwaZulu-Natal': ['Durban']
    },
    'Israel': {
      'Tel Aviv District': ['Tel Aviv'],
      'Jerusalem District': ['Jerusalem'],
      'Haifa District': ['Haifa']
    },
    'Portugal': {
      'Lisbon': ['Lisbon'],
      'Porto': ['Porto']
    },
    'Ireland': {
      'Leinster': ['Dublin'],
      'Munster': ['Cork', 'Limerick']
    },
    'Russia': {
      'Moscow': ['Moscow'],
      'Saint Petersburg': ['Saint Petersburg']
    }
  };

  const getCountries = () => Object.keys(locationData);
  const getStates = (country) => country && locationData[country] ? Object.keys(locationData[country]) : [];
  const getCities = (country, state) => country && state && locationData[country]?.[state] ? locationData[country][state] : [];

  // Steps definition
  // Tiny in-file translator. The page predates the project's i18n catalog
  // and is too long to migrate piecemeal — so we just toggle each string
  // pair inline. `tr(zh, en)` returns the right one based on the active
  // language and is cheap to inline anywhere.
  const tr = (zh, en) => (language === 'zh' ? zh : en);

  const steps = [
    {
      id: 'firstName',
      question: tr('请问您的名字？', "What's your first name?"),
      subtitle: tr('我们先从基础信息开始', "Let's start with the basics"),
      field: 'firstName',
      type: 'text',
      placeholder: tr('请输入您的名字...', 'Your first name...'),
      icon: User,
      validation: (value) => {
        if (!value.trim()) return tr('请输入名字', 'Please enter your first name');
        if (value.trim().length < 2) return tr('名字至少 2 个字符', 'First name must be at least 2 characters');
        return null;
      }
    },
    {
      id: 'lastName',
      question: tr('您的姓氏呢？', 'And your last name?'),
      subtitle: tr('基础信息快填完了', 'Almost there with the basics'),
      field: 'lastName',
      type: 'text',
      placeholder: tr('请输入您的姓氏...', 'Your last name...'),
      icon: User,
      validation: (value) => {
        if (!value.trim()) return tr('请输入姓氏', 'Please enter your last name');
        if (value.trim().length < 2) return tr('姓氏至少 2 个字符', 'Last name must be at least 2 characters');
        return null;
      }
    },
    {
      id: 'email',
      question: tr('您的电子邮箱？', "What's your email?"),
      subtitle: tr('我们会用它作为您的账号', "We'll use this for your account"),
      field: 'email',
      type: 'email',
      placeholder: 'name@example.com',
      icon: Mail,
      validation: (value) => {
        if (!value.trim()) return tr('请输入邮箱', 'Please enter your email');
        if (!/\S+@\S+\.\S+/.test(value)) return tr('请输入有效的邮箱地址', 'Please enter a valid email address');
        return null;
      }
    },
    {
      id: 'password',
      question: tr('设置密码', 'Create a password'),
      subtitle: tr('请使用安全且容易记住的密码', 'Make it strong and memorable'),
      field: 'password',
      type: 'password',
      placeholder: tr('至少 8 位字符...', 'At least 8 characters...'),
      icon: Lock,
      validation: (value) => {
        if (!value) return tr('请输入密码', 'Please enter a password');
        if (value.length < 8) return tr('密码至少 8 位字符', 'Password must be at least 8 characters');
        return null;
      }
    },
    {
      id: 'confirmPassword',
      question: tr('确认密码', 'Confirm your password'),
      subtitle: tr('再输入一次以确认', 'Just to make sure'),
      field: 'confirmPassword',
      type: 'password',
      placeholder: tr('请再次输入密码...', 'Re-enter your password...'),
      icon: Lock,
      validation: (value) => {
        if (!value) return tr('请确认您的密码', 'Please confirm your password');
        if (value !== formData.password) return tr('两次输入的密码不一致', 'Passwords do not match');
        return null;
      }
    },
    {
      id: 'companyContact',
      question: tr('联系方式', 'Contact Info'),
      subtitle: tr('电话号码', 'Phone Number'),
      field: 'companyContact',
      type: 'companyContact',
      icon: Building2,
      validation: () => {
        if (!formData.phone.trim()) return tr('请输入电话号码', 'Please enter your phone number');
        return null;
      }
    },
    {
      id: 'location',
      question: tr('您所在的位置？', 'Where are you located?'),
      subtitle: tr('您的工作地点', 'Your business location'),
      field: 'location',
      type: 'location',
      icon: MapPin,
      validation: () => {
        if (!formData.country) return tr('请选择国家', 'Please select your country');
        return null;
      }
    },
    // Plan selection step (external matchmakers only)
    ...(isExternal ? [{
      id: 'selectPlan',
      question: tr('选择您的订阅方案', 'Choose your subscription plan'),
      subtitle: tr('选择一个方案开启您的红娘事业', 'Select a plan to get started with your matchmaking business'),
      field: 'selectedPlan',
      type: 'planSelection',
      icon: CreditCard,
      validation: () => {
        if (!selectedPlanId) return tr('请选择一个方案以继续', 'Please select a plan to continue');
        return null;
      }
    }] : []),
    // Payment step (external matchmakers only)
    ...(isExternal ? [{
      id: 'payment',
      question: tr('完成付款', 'Complete Payment'),
      subtitle: tr('选择付款方式并提交付款', 'Choose a payment method and send payment'),
      field: 'payment',
      type: 'payment',
      icon: DollarSign,
      validation: () => null // Payment is informational, no validation needed
    }] : []),
    {
      id: 'ssn',
      question: tr('社会安全号 (SSN)', 'Social Security Number'),
      subtitle: tr('用于税务申报（W-9），加密遮罩显示', 'For tax reporting (W-9), shown masked'),
      field: 'ssn',
      type: 'ssn',
      icon: FileText,
      validation: (value) => {
        const digits = (value || '').replace(/\D/g, '');
        if (!digits) return tr('请填写社安号', 'Please enter your SSN');
        if (digits.length !== 9) return tr('社安号应为 9 位数字', 'SSN must be 9 digits');
        return null;
      }
    },
    {
      id: 'terms',
      question: tr('就快完成了！', 'Almost there!'),
      subtitle: tr('请阅读并接受协议条款', 'Review and accept our terms'),
      field: 'agreedToTerms',
      type: 'checkbox',
      validation: (value) => {
        if (!value) return tr('请勾选同意条款以继续', 'Please agree to the terms to continue');
        return null;
      }
    }
  ].filter(s => !['password', 'confirmPassword', 'location', 'ssn'].includes(s.id));

  // Focus input when step changes
  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [currentStep]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Editing the email after a check resets the confirmation gate so the
    // next "Continue" press re-validates against the backend.
    if (name === 'email' && emailCheck.status !== 'idle') {
      setEmailCheck({ status: 'idle', loading: false, profile: null });
    }
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validateCurrentStep = () => {
    const step = steps[currentStep];
    if (step.type === 'location' || step.type === 'companyContact' || step.type === 'planSelection' || step.type === 'payment') {
      const error = step.validation();
      if (error) {
        setErrors({ ...errors, [step.field]: error });
        return false;
      }
    } else {
      const error = step.validation(formData[step.field]);
      if (error) {
        setErrors({ ...errors, [step.field]: error });
        return false;
      }
    }
    return true;
  };

  const handleNext = async () => {
    if (!validateCurrentStep()) return;

    // Gate the email step: hit the backend to find out whether the address
    // is free, a member account (allow with confirmation), or another staff
    // account (block). We skip the call once the user has already confirmed
    // the same email in this session.
    const stepData = steps[currentStep];
    if (stepData.id === 'email') {
      const email = (formData.email || '').trim().toLowerCase();
      if (email && email !== confirmedMemberEmail) {
        try {
          setEmailCheck({ status: 'idle', loading: true, profile: null });
          const res = await api.get('/public/auth/check-matchmaker-email', { params: { email } });
          const status = res.data?.status || 'available';
          if (status === 'taken') {
            setEmailCheck({ status: 'taken', loading: false, profile: null });
            setErrors(prev => ({ ...prev, email: tr('邮箱已被注册', 'Email already registered') }));
            return;
          }
          if (status === 'profile_member') {
            // Pause here; the confirmation card asks the user to acknowledge
            // it's the same person before promoting their member account.
            setEmailCheck({ status: 'profile_member', loading: false, profile: res.data.profile });
            return;
          }
          setEmailCheck({ status: 'available', loading: false, profile: null });
        } catch (err) {
          // Fail open — the register endpoint itself will reject duplicates.
          setEmailCheck({ status: 'available', loading: false, profile: null });
        }
      }
    }

    if (currentStep < steps.length - 1) {
      setDirection('next');
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setDirection('prev');
      setCurrentStep(currentStep - 1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !['checkbox', 'location', 'companyContact', 'planSelection', 'payment'].includes(steps[currentStep].type)) {
      e.preventDefault();
      if (currentStep === steps.length - 1) {
        handleSubmit(e);
      } else {
        handleNext();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateCurrentStep()) return;

    // The password step is hidden on this flow — if no password was set, create a
    // strong random one. The admin then provisions a temporary password via the
    // "welcome email" action in /admin/affiliates.
    const effectivePassword = formData.password
      || (Math.random().toString(36).slice(-8) + Math.random().toString(36).slice(-4).toUpperCase() + 'A1!');

    setLoading(true);

    const result = await register({
      name: `${formData.firstName} ${formData.lastName}`.trim(),
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      password: effectivePassword,
      phone: formData.phone,
      company_name: formData.companyName || null,
      country: formData.country,
      state: formData.state,
      city: formData.city,
      location: [formData.city, formData.state, formData.country].filter(Boolean).join(', '),
      referral_code: formData.referral_code || undefined,
      ssn: (formData.ssn || '').replace(/\D/g, '') || undefined,
      matchmakerType: isExternal ? 'external' : 'internal',
      ...(isExternal && selectedPlanId && { planId: selectedPlanId }),
      ...(signatureData && {
        signature: {
          name: signatureData.name,
          fontFamily: signatureData.fontFamily,
          signedAt: signatureData.signedAt
        }
      })
    });

    if (result.success) {
      // Upload W9 file if provided
      if (formData.w9File && result.userId) {
        try {
          const w9FormData = new FormData();
          w9FormData.append('w9', formData.w9File);
          w9FormData.append('user_id', result.userId);
          await api.post('/auth/upload-w9', w9FormData, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (w9Err) {
          console.warn('W9 upload failed:', w9Err);
          // Don't block registration if W9 upload fails
        }
      }
      toast.success('Account created successfully!');
      navigate('/');
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  // Get selected plan details for payment step
  const selectedPlan = availablePlans.find(p => p.id === selectedPlanId);


  // ─── Presentation helpers (Luxe editorial) ──────────────────────────────────
  const pad2 = (n) => String(n).padStart(2, '0');
  // Short label for the step index + a one-line "why we ask" for each step,
  // so first-time partners scanning the QR code know what each item is for.
  const stepMeta = {
    firstName:       { label: tr('名字', 'First name'),     why: tr('用于您的红娘档案，以及合伙人协议上的签名。', 'Used on your matchmaker profile and to sign the partner agreement.') },
    lastName:        { label: tr('姓氏', 'Last name'),      why: tr('与名字一起组成您的协议签名。', 'Together with your first name, this forms your agreement signature.') },
    email:           { label: tr('邮箱', 'Email'),          why: tr('这是您的登录账号，我们也会通过它发送账号与登录信息。', 'This becomes your login, and is where we send your account details.') },
    password:        { label: tr('密码', 'Password'),       why: tr('用于登录您的红娘后台。', 'Used to sign in to your matchmaker workspace.') },
    confirmPassword: { label: tr('确认密码', 'Confirm'),    why: tr('再输一次，避免输错。', 'Typed twice to avoid typos.') },
    companyContact:  { label: tr('电话', 'Phone'),          why: tr('方便团队与您联系、确认合作细节，不会公开显示。', 'So our team can reach you about the partnership. It is never shown publicly.') },
    location:        { label: tr('所在地', 'Location'),     why: tr('帮助我们为您对接附近的会员。', 'Helps us connect you with members nearby.') },
    selectPlan:      { label: tr('方案', 'Plan'),           why: tr('方案决定您可管理的会员数量与可用功能。', 'Your plan sets how many members you can manage and which tools you get.') },
    payment:         { label: tr('付款', 'Payment'),        why: tr('付款经管理员确认后，您的订阅即会开通。', 'Your subscription is activated once an administrator confirms payment.') },
    ssn:             { label: tr('社安号', 'SSN'),          why: tr('仅用于佣金报税（W-9），加密保存。', 'Only used for commission tax reporting (W-9), stored encrypted.') },
    w9Upload:        { label: 'W-9',                         why: tr('用于佣金报税，可稍后上传。', 'For commission tax reporting; you can upload it later.') },
    terms:           { label: tr('签署协议', 'Agreement'),  why: tr('请核对资料，签署合伙人协议后即可提交申请。', 'Check your details, sign the partner agreement, then submit.') },
  };
  const labelOf = (s) => (stepMeta[s.id] && stepMeta[s.id].label) || s.id;
  const whyOf = (s) => stepMeta[s.id] && stepMeta[s.id].why;

  const errLine = (msg, center) => (msg ? (
    <div role="alert" className="lx-fade" style={{
      fontFamily: L.sans, fontSize: 13.5, color: L.danger, marginTop: 12,
      letterSpacing: '.02em', display: 'flex', alignItems: 'center', gap: 8,
      justifyContent: center ? 'center' : 'flex-start',
    }}>
      <span style={{ width: 14, height: 1, background: L.danger, flexShrink: 0 }} />
      {msg}
    </div>
  ) : null);

  const fieldKicker = (text, extra = {}) => (
    <div style={kicker({ fontSize: 12, color: MUTE, marginBottom: 2, ...extra })}>{text}</div>
  );

  const bigInput = (hasErr, extra = {}) => ({
    fontFamily: L.serif, fontSize: isDesktop ? 28 : 22, fontWeight: 400, padding: '12px 0',
    borderBottomColor: hasErr ? L.danger : L.ink,
    ...extra,
  });

  const selectChevron = (
    <ChevronDown size={16} strokeWidth={1.25} style={{
      position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)',
      pointerEvents: 'none', color: MUTE,
    }} />
  );

  const spinner = (label) => (
    <div style={{ textAlign: 'center', padding: '36px 0' }}>
      <div style={{
        width: 28, height: 28, border: `1px solid ${L.line}`, borderTopColor: L.ink,
        borderRadius: '50%', animation: 'lxSpin .9s linear infinite', margin: '0 auto 14px',
      }} />
      <p style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 17, color: MUTE, margin: 0 }}>{label}</p>
    </div>
  );

  const noteBox = (children, extra = {}) => (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px',
      background: L.paper2, border: `1px solid ${L.lineSoft}`, borderRadius: L.radius,
      fontFamily: L.sans, fontSize: 13.5, color: L.ink2, lineHeight: 1.6, ...extra,
    }}>
      <Info size={15} strokeWidth={1.25} color={L.goldDeep} style={{ flexShrink: 0, marginTop: 3 }} />
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );

  const cycleLabel = (cycle) => (cycle === 'yearly' ? tr('年', 'year') : cycle === 'quarterly' ? tr('季', 'quarter') : tr('月', 'month'));

  const renderInputField = () => {
    switch (currentStepData.type) {
      case 'companyContact':
        return (
          <div>
            {/* Company Name field hidden per request */}
            {fieldKicker(tr('电话号码 · 必填', 'Phone number · required'))}
            <div style={{ position: 'relative' }}>
              <Phone size={17} strokeWidth={1.25} color={MUTE} style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }} />
              <input
                ref={inputRef}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                name="phone"
                className="lx-input"
                value={formData.phone}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder={tr('电话号码 *', 'Phone Number *')}
                style={bigInput(!!errors.companyContact, { paddingLeft: 30 })}
              />
            </div>
            {errLine(errors.companyContact)}
          </div>
        );

      case 'location': {
        const availableCities = getCities(formData.country, formData.state);
        const filteredCities = availableCities.filter(city =>
          city.toLowerCase().includes(citySearch.toLowerCase())
        );
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
            {/* Country */}
            <div>
              {fieldKicker(tr('国家 / 地区', 'Country'))}
              <div style={{ position: 'relative' }}>
                <select
                  name="country"
                  className="lx-input"
                  value={formData.country}
                  onChange={(e) => {
                    setFormData({ ...formData, country: e.target.value, state: '', city: '' });
                    setCitySearch('');
                    setShowCityDropdown(false);
                  }}
                  style={bigInput(!!errors.location && !formData.country, { fontFamily: L.sans, fontWeight: 300, fontSize: 17, color: formData.country ? L.ink : MUTE })}
                >
                  <option value="">{language === 'zh' ? '选择国家...' : 'Select Country...'}</option>
                  {getCountries().map(countryName => (
                    <option key={countryName} value={countryName}>{translateLocation(countryName, language)}</option>
                  ))}
                </select>
                {selectChevron}
              </div>
            </div>

            {/* State */}
            {formData.country && getStates(formData.country).length > 0 && (
              <div className="lx-fade-up">
                {fieldKicker(tr('省 / 州', 'State / Province'))}
                <div style={{ position: 'relative' }}>
                  <select
                    name="state"
                    className="lx-input"
                    value={formData.state}
                    onChange={(e) => {
                      setFormData({ ...formData, state: e.target.value, city: '' });
                      setCitySearch('');
                      setShowCityDropdown(false);
                    }}
                    style={bigInput(false, { fontFamily: L.sans, fontWeight: 300, fontSize: 17, color: formData.state ? L.ink : MUTE })}
                  >
                    <option value="">{language === 'zh' ? '选择省/州...' : 'Select State/Province...'}</option>
                    {getStates(formData.country).map(stateName => (
                      <option key={stateName} value={stateName}>{translateLocation(stateName, language)}</option>
                    ))}
                  </select>
                  {selectChevron}
                </div>
              </div>
            )}

            {/* Searchable City Dropdown */}
            {formData.country && formData.state && (
              <div ref={cityDropdownRef} className="lx-fade-up" style={{ position: 'relative' }}>
                {fieldKicker(tr('城市', 'City'))}
                <div style={{ position: 'relative' }}>
                  <Search size={16} strokeWidth={1.25} color={MUTE} style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }} />
                  <input
                    type="text"
                    className="lx-input"
                    value={citySearch || formData.city}
                    onChange={(e) => {
                      setCitySearch(e.target.value);
                      setFormData({ ...formData, city: e.target.value });
                      setShowCityDropdown(true);
                    }}
                    onFocus={() => setShowCityDropdown(true)}
                    placeholder={language === 'zh' ? '搜索城市...' : 'Search city...'}
                    style={bigInput(false, { fontFamily: L.sans, fontWeight: 300, fontSize: 17, paddingLeft: 28 })}
                  />
                </div>

                {/* City dropdown list */}
                {showCityDropdown && availableCities.length > 0 && (
                  <div className="lx-fade" style={{
                    position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
                    background: L.card, border: `1px solid ${L.line}`, borderRadius: L.radius,
                    boxShadow: 'none', maxHeight: 240, overflowY: 'auto', zIndex: 30,
                  }}>
                    {filteredCities.length > 0 ? (
                      filteredCities.map(city => {
                        const on = formData.city === city;
                        return (
                          <div
                            key={city}
                            className="lx-mm-opt"
                            onClick={() => {
                              setFormData({ ...formData, city });
                              setCitySearch(city);
                              setShowCityDropdown(false);
                            }}
                            style={{
                              padding: '14px 16px', cursor: 'pointer', fontFamily: L.sans, fontSize: 14.5,
                              color: L.ink, fontWeight: on ? 500 : 300,
                              background: on ? L.paper2 : 'transparent',
                              borderBottom: `1px solid ${L.lineSoft}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                            }}
                          >
                            {translateLocation(city, language)}
                            {on && <Check size={13} strokeWidth={1.5} color={L.gold} />}
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ padding: '14px 16px', fontFamily: L.serif, fontStyle: 'italic', fontSize: 16, color: MUTE, textAlign: 'center' }}>
                        {language === 'zh' ? '未找到城市' : 'No cities found'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {errLine(errors.location)}
          </div>
        );
      }

      case 'planSelection':
        return (
          <div>
            {plansLoading ? spinner(tr('正在加载方案...', 'Loading plans...')) : availablePlans.length === 0 ? (
              <p style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 18, color: MUTE, textAlign: 'center', padding: '28px 0', margin: 0 }}>
                {tr('暂无可用方案，请稍后再试。', 'No plans available at the moment. Please try again later.')}
              </p>
            ) : (
              <div role="radiogroup" aria-label={currentStepData.question} style={{
                display: 'grid',
                gridTemplateColumns: isDesktop && availablePlans.length >= 2 ? 'repeat(auto-fit, minmax(220px, 1fr))' : 'minmax(0, 1fr)',
                gap: 14,
              }}>
                {availablePlans.map(plan => {
                  const isSelected = selectedPlanId === plan.id;
                  const features = typeof plan.features === 'string' ? JSON.parse(plan.features) : (plan.features || []);
                  const billingLabel = plan.billing_cycle === 'yearly' ? tr('/年', '/year') : plan.billing_cycle === 'quarterly' ? tr('/季', '/quarter') : tr('/月', '/month');
                  const pick = () => {
                    setSelectedPlanId(plan.id);
                    if (errors.selectedPlan) setErrors({ ...errors, selectedPlan: null });
                  };

                  return (
                    <div
                      key={plan.id}
                      role="radio"
                      aria-checked={isSelected}
                      tabIndex={0}
                      className="lx-mm-card"
                      onClick={pick}
                      onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); pick(); } }}
                      style={{
                        position: 'relative', cursor: 'pointer', textAlign: 'left',
                        background: L.card,
                        border: `1px solid ${isSelected ? L.ink : (errors.selectedPlan ? 'rgba(158,43,37,.35)' : 'rgba(17,17,17,.14)')}`,
                        borderRadius: L.radius, padding: '22px 20px 20px',
                        boxShadow: 'none',
                        display: 'flex', flexDirection: 'column',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ minWidth: 0 }}>
                          {plan.is_popular === 1 && (
                            <div style={kicker({ fontSize: 12, color: L.goldDeep, marginBottom: 8 })}>
                              {tr('最受欢迎', 'Most Popular')}
                            </div>
                          )}
                          <h3 style={{ fontFamily: L.serif, fontSize: 24, fontWeight: 500, color: L.ink, margin: 0, lineHeight: 1.15 }}>
                            {plan.name}
                          </h3>
                        </div>
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                          border: `1px solid ${isSelected ? L.ink : L.line}`,
                          background: isSelected ? L.ink : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: `all .3s ${L.ease}`,
                        }}>
                          {isSelected && <Check size={12} strokeWidth={2} color={L.paper} />}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 14 }}>
                        <span style={{ fontFamily: L.serif, fontSize: 40, lineHeight: 1, color: L.ink }}>
                          ${parseFloat(plan.price || 0).toFixed(0)}
                        </span>
                        <span style={{ fontFamily: L.sans, fontSize: 13.5, color: MUTE }}>{billingLabel}</span>
                      </div>

                      {plan.trial_days > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <span style={{
                            ...kicker({ fontSize: 12, color: L.goldDeep, letterSpacing: '.14em' }),
                            border: `1px solid ${L.goldSoft}`, padding: '5px 10px', borderRadius: L.radius, display: 'inline-block',
                          }}>
                            {tr(`${plan.trial_days} 天免费试用`, `${plan.trial_days}-day free trial`)}
                          </span>
                        </div>
                      )}

                      {plan.description && (
                        <p style={{ fontFamily: L.sans, fontSize: 13.5, fontWeight: 300, color: L.ink2, lineHeight: 1.6, margin: '14px 0 0' }}>
                          {plan.description}
                        </p>
                      )}

                      {(plan.max_profiles || plan.max_matches || features.length > 0) && (
                        <div style={{
                          borderTop: `1px solid ${L.line}`, marginTop: 16, paddingTop: 14,
                          display: 'flex', flexDirection: 'column', gap: 9,
                        }}>
                          {plan.max_profiles && (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontFamily: L.sans, fontSize: 13.5, color: L.ink2 }}>
                              <Check size={14} strokeWidth={1.5} color={L.gold} style={{ flexShrink: 0, marginTop: 3 }} />
                              {tr(`最多 ${plan.max_profiles} 个会员档案`, `Up to ${plan.max_profiles} profiles`)}
                            </div>
                          )}
                          {plan.max_matches && (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontFamily: L.sans, fontSize: 13.5, color: L.ink2 }}>
                              <Check size={14} strokeWidth={1.5} color={L.gold} style={{ flexShrink: 0, marginTop: 3 }} />
                              {tr(`每月最多 ${plan.max_matches} 次配对`, `Up to ${plan.max_matches} matches/month`)}
                            </div>
                          )}
                          {features.map((feature, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontFamily: L.sans, fontSize: 13.5, color: L.ink2 }}>
                              <Check size={14} strokeWidth={1.5} color={L.gold} style={{ flexShrink: 0, marginTop: 3 }} />
                              {feature}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {errLine(errors.selectedPlan)}
          </div>
        );

      case 'payment':
        return (
          <div>
            {/* Selected Plan Summary */}
            {selectedPlan && (
              <div style={{
                background: L.card, border: `1px solid ${L.ink}`, borderRadius: L.radius,
                padding: '20px 22px', marginBottom: 28,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
                  <span style={kicker({ fontSize: 12, color: L.ink })}>{tr('方案摘要', 'Plan Summary')}</span>
                  <span style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 18, color: L.goldDeep }}>{selectedPlan.name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, borderTop: `1px solid ${L.line}`, paddingTop: 14 }}>
                  <span style={{ fontFamily: L.sans, fontSize: 14, color: MUTE }}>{tr('应付金额', 'Amount due')}</span>
                  <div>
                    <span style={{ fontFamily: L.serif, fontSize: 34, color: L.ink, lineHeight: 1 }}>
                      ${parseFloat(selectedPlan.price || 0).toFixed(2)}
                    </span>
                    <span style={{ fontFamily: L.sans, fontSize: 13.5, color: MUTE, marginLeft: 4 }}>
                      /{cycleLabel(selectedPlan.billing_cycle)}
                    </span>
                  </div>
                </div>
                {selectedPlan.trial_days > 0 && (
                  <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, fontFamily: L.sans, fontSize: 13.5, color: L.online }}>
                    <Clock size={15} strokeWidth={1.25} />
                    {tr(`您的 ${selectedPlan.trial_days} 天免费试用即刻开始`, `Your ${selectedPlan.trial_days}-day free trial starts immediately`)}
                  </div>
                )}
              </div>
            )}

            {/* Payment Methods */}
            <div style={{ marginBottom: 18 }}>
              <div style={kicker({ fontSize: 12, color: L.ink, marginBottom: 12 })}>{tr('付款方式', 'Payment Methods')}</div>

              {/* China users: surface a short note about PayPal being hidden so
                  they don't wonder why it's gone. */}
              {isInChinaSync() && noteBox(
                tr('PayPal 在您的地区暂不可用,请使用银行转账或其它本地付款方式。',
                   'PayPal is unavailable in your region. Please use bank transfer or another local method.'),
                { marginBottom: 12 }
              )}

              {paymentLoading ? spinner(tr('正在加载付款方式...', 'Loading payment methods...')) : paymentInstructions.length === 0 ? (
                <p style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 17, color: MUTE, textAlign: 'center', padding: '24px 0', margin: 0, borderTop: `1px solid ${L.line}`, borderBottom: `1px solid ${L.line}` }}>
                  {tr('尚未配置任何付款方式，请联系管理员。', 'No payment methods configured yet. Please contact the administrator.')}
                </p>
              ) : (
                <div style={{ borderTop: `1px solid ${L.ink}` }}>
                  {paymentInstructions.map((method) => {
                    const isExpanded = expandedMethod === method.payment_method;
                    return (
                      <div key={method.payment_method} style={{ borderBottom: `1px solid ${L.line}` }}>
                        {/* Method Header */}
                        <button
                          type="button"
                          aria-expanded={isExpanded}
                          className="lx-mm-opt"
                          onClick={() => setExpandedMethod(isExpanded ? null : method.payment_method)}
                          style={{
                            width: '100%', minHeight: 56, padding: '14px 4px', display: 'flex',
                            justifyContent: 'space-between', alignItems: 'center', gap: 12,
                            background: isExpanded ? L.paper2 : 'transparent', border: 0, cursor: 'pointer', textAlign: 'left',
                          }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <DollarSign size={16} strokeWidth={1.25} color={L.goldDeep} />
                            <span style={{ fontFamily: L.serif, fontSize: 20, color: L.ink }}>{method.display_name}</span>
                          </span>
                          {isExpanded ? <ChevronUp size={16} strokeWidth={1.25} color={MUTE} /> : <ChevronDown size={16} strokeWidth={1.25} color={MUTE} />}
                        </button>

                        {/* Method Details */}
                        {isExpanded && (
                          <div className="lx-fade" style={{ padding: '4px 4px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {Object.entries(method.account_details).map(([key, value]) => {
                              if (!value) return null;
                              const label = key
                                .replace(/_/g, ' ')
                                .replace(/\b\w/g, l => l.toUpperCase());

                              if (key === 'instructions') {
                                return (
                                  <div key={key} style={{
                                    marginTop: 6, padding: '12px 14px', background: L.paper2,
                                    borderLeft: `2px solid ${L.goldSoft}`, fontFamily: L.sans, fontSize: 13.5,
                                    color: L.ink2, lineHeight: 1.6, whiteSpace: 'pre-wrap',
                                  }}>
                                    {value}
                                  </div>
                                );
                              }

                              return (
                                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16 }}>
                                  <span style={{ fontFamily: L.sans, fontSize: 13.5, color: MUTE }}>{label}</span>
                                  <span style={{ fontFamily: L.sans, fontSize: 14.5, color: L.ink, fontWeight: 500, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-all', userSelect: 'all' }}>{value}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Info Note */}
            {noteBox(selectedPlan?.trial_days > 0
              ? tr(`注册后，您的 ${selectedPlan.trial_days} 天免费试用将立即开始，试用期结束时需付款。`,
                   `Your ${selectedPlan.trial_days}-day free trial will begin immediately after registration. Payment will be due at the end of your trial period.`)
              : tr('管理员确认收款后，您的订阅即会开通。请使用以上任一方式付款。',
                   'Your subscription will be activated once payment is confirmed by the administrator. Please send payment using one of the methods above.'))}
          </div>
        );

      case 'ssn': {
        const ssnRaw = (formData.ssn || '').replace(/\D/g, '').slice(0, 9);
        const ssnFmt = ssnRaw.replace(/^(\d{0,3})(\d{0,2})(\d{0,4}).*$/, (m, a, b, c) => [a, b, c].filter(Boolean).join('-'));
        return (
          <div>
            {fieldKicker(tr('社会安全号 (SSN)', 'Social Security Number'))}
            <div style={{ position: 'relative' }}>
              <input
                type={showSsn ? 'text' : 'password'}
                inputMode="numeric"
                autoComplete="off"
                className="lx-input"
                value={showSsn ? ssnFmt : ssnRaw}
                onChange={(e) => setFormData(prev => ({ ...prev, ssn: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                placeholder="XXX-XX-XXXX"
                maxLength={showSsn ? 11 : 9}
                style={bigInput(!!errors.ssn, { paddingRight: 44, letterSpacing: showSsn ? '.12em' : '.3em', fontVariantNumeric: 'tabular-nums' })}
              />
              <button
                type="button"
                onClick={() => setShowSsn(s => !s)}
                aria-label={showSsn ? tr('隐藏', 'Hide') : tr('显示', 'Show')}
                style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: MUTE, padding: 10 }}
              >
                {showSsn ? <EyeOff size={18} strokeWidth={1.25} /> : <Eye size={18} strokeWidth={1.25} />}
              </button>
            </div>
            {errLine(errors.ssn)}
            <p style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 16, color: MUTE, margin: '16px 0 0' }}>
              {tr('您的社安号将被安全加密保存。', 'Your SSN is stored securely and encrypted.')}
            </p>
          </div>
        );
      }

      case 'w9Upload':
        return (
          <div>
            <p style={{ fontFamily: L.sans, fontSize: 14, fontWeight: 300, color: L.ink2, lineHeight: 1.6, margin: '0 0 18px' }}>
              {tr('上传已填写好的 W-9 表用于税务申报。此为可选项，您也可以稍后在设置中上传。', 'Upload your completed W-9 form for tax reporting. This is optional and can be uploaded later from your settings.')}
            </p>

            {/* How-to-fill steps (download link in step 1) */}
            <div style={{ background: L.paper2, border: `1px solid ${L.lineSoft}`, borderRadius: L.radius, padding: '16px 18px', marginBottom: 18 }}>
              <div style={kicker({ fontSize: 12, color: L.ink, marginBottom: 10 })}>{tr('如何填写', 'How to fill it out')}</div>
              <ol style={{ margin: 0, paddingLeft: 18, fontFamily: L.sans, fontSize: 13.5, color: L.ink2, lineHeight: 1.85 }}>
                <li>
                  {tr('点这里下载官方 W-9 空白表：', 'Click here to download the official blank W-9 form: ')}
                  <a href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" target="_blank" rel="noopener noreferrer"
                    style={{ color: L.ink, fontWeight: 500, textDecoration: 'underline', wordBreak: 'break-all' }}>
                    https://www.irs.gov/pub/irs-pdf/fw9.pdf
                  </a>
                </li>
                <li>{tr('第1行填您的个人法定姓名；第2行（公司名）个人留空。', 'Line 1: your legal name. Line 2 (business name): leave blank for individuals.')}</li>
                <li>{tr('第3行勾选「Individual/sole proprietor（个人/独资）」。', 'Line 3: check "Individual/sole proprietor".')}</li>
                <li>{tr('第5-6行填写地址、城市、州和邮编。', 'Lines 5-6: enter your address, city, state and ZIP.')}</li>
                <li>{tr('Part I 填写您的 SSN 社会安全号（如以公司名义则填 EIN）。', 'Part I: enter your SSN (or EIN if registering as a business).')}</li>
                <li>{tr('Part II 签名并填写日期。', 'Part II: sign and date the form.')}</li>
                <li>{tr('保存为 PDF 或拍照，然后在下方上传。', 'Save as PDF or take a photo, then upload it below.')}</li>
              </ol>
            </div>

            {formData.w9File ? (
              <div style={{
                padding: '14px 16px', background: L.card, border: `1px solid ${L.ink}`, borderRadius: L.radius,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <Check size={16} strokeWidth={1.5} color={L.gold} style={{ flexShrink: 0 }} />
                  <span style={{ fontFamily: L.sans, fontSize: 14, color: L.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formData.w9File.name}
                  </span>
                  <span style={{ fontFamily: L.sans, fontSize: 13, color: MUTE, flexShrink: 0 }}>
                    ({(formData.w9File.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
                <button
                  type="button"
                  aria-label={tr('移除', 'Remove')}
                  onClick={() => { setFormData(prev => ({ ...prev, w9File: null })); setW9Preview(null); }}
                  className="lx-icon-btn"
                  style={{ ...btn.icon, width: 36, height: 36, flexShrink: 0 }}
                >
                  <X size={15} strokeWidth={1.25} />
                </button>
              </div>
            ) : (
              <label className="lx-mm-drop" style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                padding: '28px 20px', border: `1px dashed ${MUTE}`, borderRadius: L.radius,
                cursor: 'pointer', background: 'transparent', textAlign: 'center',
              }}>
                <Upload size={22} strokeWidth={1.25} color={L.ink} />
                <span style={{ fontFamily: L.serif, fontSize: 20, color: L.ink }}>
                  {tr('点击上传 W-9 表', 'Click to upload W-9 form')}
                </span>
                <span style={kicker({ fontSize: 12 })}>
                  {tr('PDF / JPG / PNG —— 最大 10 MB', 'PDF, JPG, PNG — Max 10 MB')}
                </span>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 10 * 1024 * 1024) {
                      toast.error('File too large. Maximum 10 MB.');
                      return;
                    }
                    setFormData(prev => ({ ...prev, w9File: file }));
                    setW9Preview(file.name);
                  }}
                />
              </label>
            )}

            <p style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 16, color: MUTE, margin: '16px 0 0' }}>
              {tr('您可以跳过此步骤，稍后在设置中上传。', 'You can skip this step and upload later from your affiliate settings.')}
            </p>
          </div>
        );

      case 'checkbox': {
        const summary = [
          { k: tr('姓名', 'Name'), v: `${formData.firstName} ${formData.lastName}`.trim() || '—' },
          { k: tr('邮箱', 'Email'), v: formData.email || '—' },
          ...(formData.companyName ? [{ k: tr('公司', 'Company'), v: formData.companyName }] : []),
          { k: tr('电话', 'Phone'), v: formData.phone || '—' },
          { k: tr('类型', 'Type'), v: language === 'zh' ? (isExternal ? '外部合伙人' : '合伙人') : (isExternal ? 'External Partner' : 'Partner') },
        ];
        return (
          <div>
            {/* Referral Code Display */}
            {formData.referral_code && (
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap', marginBottom: 22,
                paddingBottom: 14, borderBottom: `1px solid ${L.line}`,
              }}>
                <span style={kicker({ fontSize: 12, color: L.goldDeep })}>{tr('推荐码', 'Referral Code')}</span>
                <span style={{ fontFamily: L.serif, fontSize: 20, color: L.ink, letterSpacing: '.06em', wordBreak: 'break-all' }}>{formData.referral_code}</span>
              </div>
            )}

            {/* Summary */}
            <div style={kicker({ fontSize: 12, color: L.ink, marginBottom: 10 })}>{tr('请核对您的资料', 'Please check your details')}</div>
            <div style={{
              display: 'grid', gridTemplateColumns: isDesktop ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
              borderTop: `1px solid ${L.ink}`, marginBottom: 30,
            }}>
              {summary.map((r, i) => (
                <div key={r.k} style={{
                  padding: '14px 0', borderBottom: `1px solid ${L.line}`,
                  paddingLeft: isDesktop && i % 2 === 1 ? 20 : 0,
                  borderLeft: isDesktop && i % 2 === 1 ? `1px solid ${L.line}` : 'none',
                }}>
                  <div style={kicker({ fontSize: 12, marginBottom: 6 })}>{r.k}</div>
                  <div style={{ fontFamily: L.serif, fontSize: 19, color: L.ink, lineHeight: 1.3, wordBreak: 'break-word' }}>{r.v}</div>
                </div>
              ))}
              {isExternal && selectedPlan && (
                <div style={{ gridColumn: '1 / -1', padding: '14px 0', borderBottom: `1px solid ${L.line}` }}>
                  <div style={kicker({ fontSize: 12, marginBottom: 6 })}>{tr('所选方案', 'Selected Plan')}</div>
                  <div style={{ fontFamily: L.serif, fontSize: 19, color: L.ink, lineHeight: 1.3 }}>
                    {selectedPlan.name} - ${parseFloat(selectedPlan.price || 0).toFixed(2)}/{cycleLabel(selectedPlan.billing_cycle)}
                    {selectedPlan.trial_days > 0 && (
                      <span style={{ fontFamily: L.sans, fontSize: 13.5, color: L.online, marginLeft: 8 }}>
                        ({tr(`${selectedPlan.trial_days} 天免费试用`, `${selectedPlan.trial_days}-day free trial`)})
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Terms Checkbox — opens the signature modal; it can only be
                checked by signing the partner agreement in the modal. */}
            <div
              role="checkbox"
              aria-checked={!!formData.agreedToTerms}
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); e.currentTarget.click(); } }}
              onClick={() => {
                if (!formData.agreedToTerms) {
                  setShowSignatureModal(true);
                } else {
                  setFormData({ ...formData, agreedToTerms: false });
                  setSignatureData(null);
                  // Back to the default signature font (not '') so the
                  // 签署并同意 button works again if they re-open the agreement.
                  setSignatureStyle(SIGNATURE_FONT);
                }
              }}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 14, cursor: 'pointer',
                padding: '18px 20px', borderRadius: L.radius, minHeight: 56, boxSizing: 'border-box',
                background: formData.agreedToTerms ? L.card : 'transparent',
                border: `1px solid ${formData.agreedToTerms ? L.ink : (errors.agreedToTerms ? L.danger : L.line)}`,
                transition: `all .35s ${L.ease}`,
              }}
            >
              <div style={{
                width: 22, height: 22, marginTop: 1, borderRadius: L.radius,
                border: `1px solid ${L.ink}`,
                background: formData.agreedToTerms ? L.ink : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: `all .3s ${L.ease}`,
              }}>
                {formData.agreedToTerms && <Check size={13} strokeWidth={2} color={L.paper} />}
              </div>
              <span style={{ fontFamily: L.sans, fontSize: 14.5, color: L.ink2, lineHeight: 1.6, fontWeight: 300 }}>
                {language === 'zh' ? (
                  <>
                    我同意
                    <span style={{ color: L.ink, fontWeight: 500 }}>《服务条款》</span>
                    与
                    <span style={{ color: L.ink, fontWeight: 500 }}>《隐私政策》</span>
                  </>
                ) : (
                  <>
                    I agree to the{' '}
                    <span style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 17, color: L.ink }}>Terms of Service</span>
                    {' '}and{' '}
                    <span style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 17, color: L.ink }}>Privacy Policy</span>
                  </>
                )}
                {!formData.agreedToTerms && (
                  <span style={{ display: 'block', marginTop: 4, fontFamily: L.sans, fontSize: 13, color: MUTE }}>
                    {tr('点击阅读并签署合伙人协议', 'Tap to read and sign the partner agreement')}
                  </span>
                )}
              </span>
            </div>

            {/* Signature Preview (after signing) */}
            {formData.agreedToTerms && signatureData && (
              <div className="lx-fade-up" style={{
                marginTop: 18, padding: '22px 24px', background: L.card,
                border: `1px solid ${L.line}`, borderRadius: L.radius, textAlign: 'center',
              }}>
                <div style={kicker({ fontSize: 12, color: L.goldDeep, marginBottom: 10 })}>{tr('签名', 'Signed as')}</div>
                <div style={{ fontFamily: signatureData.fontFamily, fontSize: 32, color: L.ink, lineHeight: 1.25, wordBreak: 'break-word' }}>
                  {signatureData.name}
                </div>
                <div style={{ width: 120, height: 1, background: L.line, margin: '12px auto 10px' }} />
                <div style={{ fontFamily: L.sans, fontSize: 13, color: MUTE }}>
                  {new Date(signatureData.signedAt).toLocaleString()}
                </div>
              </div>
            )}

            {errLine(errors.agreedToTerms)}
          </div>
        );
      }

      default: {
        const fieldErr = errors[currentStepData.field];
        return (
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              <input
                ref={inputRef}
                type={currentStepData.type === 'password' && showPassword ? 'text' : currentStepData.type}
                name={currentStepData.field}
                className="lx-input"
                value={formData[currentStepData.field]}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder={currentStepData.placeholder}
                autoComplete={currentStepData.type === 'password' ? 'new-password' : 'off'}
                {...(currentStepData.type === 'email' ? { inputMode: 'email', autoCapitalize: 'none', spellCheck: false } : {})}
                style={bigInput(!!fieldErr, { paddingRight: currentStepData.type === 'password' ? 44 : 0 })}
              />
              {currentStepData.type === 'password' && (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? tr('隐藏密码', 'Hide password') : tr('显示密码', 'Show password')}
                  style={{
                    position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: MUTE, padding: 10,
                  }}
                >
                  {showPassword ? <EyeOff size={18} strokeWidth={1.25} /> : <Eye size={18} strokeWidth={1.25} />}
                </button>
              )}
            </div>
            {errLine(fieldErr)}

            {/* Email-step extras: checking hint and the member-confirm card.
                Only shown on the email step. */}
            {currentStepData.id === 'email' && emailCheck.loading && (
              <div className="lx-fade" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, fontFamily: L.serif, fontStyle: 'italic', fontSize: 16, color: MUTE }}>
                <span style={{ width: 14, height: 14, border: `1px solid ${L.line}`, borderTopColor: L.ink, borderRadius: '50%', animation: 'lxSpin .9s linear infinite', flexShrink: 0 }} />
                {tr('正在检查邮箱可用性…', 'Checking email availability…')}
              </div>
            )}
            {currentStepData.id === 'email' && emailCheck.status === 'profile_member' && emailCheck.profile && (
              <div className="lx-fade-up" style={{
                marginTop: 24, background: L.card, border: `1px solid ${L.ink}`, borderRadius: L.radius,
                padding: isDesktop ? '22px 24px' : '18px 16px', boxShadow: 'none',
              }}>
                <div style={kicker({ fontSize: 12, color: L.goldDeep, marginBottom: 14 })}>
                  {tr('该邮箱已绑定一个会员账号', 'This email is linked to a member account')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                  {emailCheck.profile.profile_photo ? (
                    <img
                      src={emailCheck.profile.profile_photo}
                      alt=""
                      style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `1px solid ${L.line}` }}
                    />
                  ) : (
                    <div style={{
                      width: 56, height: 56, borderRadius: '50%', background: L.ink, color: L.paper,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: L.serif, fontSize: 26, flexShrink: 0,
                    }}>
                      {(emailCheck.profile.first_name?.[0] || emailCheck.profile.email?.[0] || '?').toUpperCase()}
                    </div>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: L.serif, fontSize: 22, color: L.ink, lineHeight: 1.2 }}>
                      {`${emailCheck.profile.first_name || ''} ${emailCheck.profile.last_name || ''}`.trim() || emailCheck.profile.email}
                    </div>
                    <div style={{ fontFamily: L.sans, fontSize: 13.5, color: MUTE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {emailCheck.profile.email}
                    </div>
                  </div>
                </div>
                <p style={{ fontFamily: L.sans, fontSize: 14, fontWeight: 300, color: L.ink2, margin: '0 0 18px', lineHeight: 1.65 }}>
                  {tr('请确认这是您本人的会员账号——确认后此账号将同时拥有「会员」和「内部红娘」两个身份，红娘头像将自动使用您的会员头像，原档案数据保留。',
                      "Please confirm this is your own member account — once confirmed the same account will hold BOTH the member role and the internal matchmaker role. Your matchmaker avatar will reuse your member profile photo, and all existing profile data is kept.")}
                </p>
                <div style={{ display: 'flex', gap: 10, flexDirection: isDesktop ? 'row' : 'column' }}>
                  <button
                    type="button"
                    className="lx-btn"
                    onClick={() => {
                      setConfirmedMemberEmail((formData.email || '').trim().toLowerCase());
                      setEmailCheck({ status: 'available', loading: false, profile: null });
                      // The user already typed their firstName + lastName in
                      // earlier steps; respect those instead of overwriting
                      // with whatever was on the member record. Backend will
                      // persist the typed names on the upgraded row.
                      if (currentStep < steps.length - 1) {
                        setDirection('next');
                        setCurrentStep(currentStep + 1);
                      }
                    }}
                    style={{ ...btn.primary, padding: '14px 20px', minHeight: 50, letterSpacing: '.14em', flex: isDesktop ? 1 : undefined }}
                  >
                    {tr('是我本人，继续注册', "Yes, that's me — continue")}
                  </button>
                  <button
                    type="button"
                    className="lx-btn-ghost"
                    onClick={() => {
                      setEmailCheck({ status: 'idle', loading: false, profile: null });
                      setFormData(prev => ({ ...prev, email: '' }));
                      setErrors(prev => ({ ...prev, email: null }));
                      setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                    style={{ ...btn.ghost, padding: '14px 20px', minHeight: 50, letterSpacing: '.14em', flex: isDesktop ? 1 : undefined }}
                  >
                    {tr('不是我，换个邮箱', "That's not me — use another email")}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      }
    }
  };

  // Determine if current step needs wider container
  const isWideStep = currentStepData?.type === 'planSelection' || currentStepData?.type === 'payment';
  const isLast = currentStep === steps.length - 1;
  const stepCounter = `${pad2(currentStep + 1)} / ${pad2(steps.length)}`;
  const StepIcon = currentStepData.icon;
  const memberConfirmOpen = currentStepData.id === 'email' && emailCheck.status === 'profile_member' && !!emailCheck.profile;
  const typeLabel = language === 'zh'
    ? (isExternal ? '外部红娘' : '红娘')
    : (isExternal ? 'External Matchmaker' : 'Matchmaker');

  // Language toggle + sign-in link, shared by both breakpoints.
  const langToggle = (
    <div role="group" aria-label="Language" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      {[
        { code: 'zh', label: '中文' },
        { code: 'en', label: 'EN' },
      ].map((opt, i) => {
        const active = language === opt.code;
        return (
          <React.Fragment key={opt.code}>
            {i > 0 && <span style={{ width: 1, height: 11, background: L.line }} />}
            <button
              type="button"
              onClick={() => switchLanguage(opt.code)}
              className={`lx-link${active ? ' lx-active' : ''}`}
              aria-pressed={active}
              style={{
                ...kicker({ fontSize: 12.5, color: active ? L.ink : MUTE, letterSpacing: '.18em' }),
                background: 'none', borderTop: 0, borderLeft: 0, borderRight: 0, padding: '8px 0',
                borderBottom: `1px solid ${active ? L.ink : 'transparent'}`, cursor: active ? 'default' : 'pointer',
              }}
            >
              {opt.label}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
  const loginLink = (
    <Link
      to="/admin/login"
      className="lx-link"
      style={{ ...kicker({ fontSize: 12.5, color: L.goldDeep, letterSpacing: '.12em' }), textDecoration: 'none', whiteSpace: 'nowrap' }}
    >
      {language === 'zh' ? '已有账号？' : 'Already have an account?'}
    </Link>
  );

  const lockup = (size) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: Math.round(size * 0.32) }}>
      <DiamondMark size={Math.round(size * 0.78)} />
      <div style={{
        fontFamily: L.serif, fontSize: size, fontWeight: 500, letterSpacing: '.32em',
        lineHeight: 1, color: L.ink, marginRight: '-.32em',
      }}>FINDU</div>
    </div>
  );

  const pageCss = `
    @keyframes lxMmNext { from { opacity:0; transform:translateX(24px); } to { opacity:1; transform:none; } }
    @keyframes lxMmPrev { from { opacity:0; transform:translateX(-24px); } to { opacity:1; transform:none; } }
    .lx-mm-next { animation:lxMmNext .65s ${L.easeOut} backwards; }
    .lx-mm-prev { animation:lxMmPrev .65s ${L.easeOut} backwards; }
    .lx-mm .lx-input::placeholder { color:${MUTE}; }
    .lx-mm select.lx-input { appearance:none; -webkit-appearance:none; -moz-appearance:none; cursor:pointer; padding-right:28px !important; }
    .lx-mm select option { color:${L.ink}; }
    .lx-mm-opt { transition:background .2s ${L.ease}; }
    .lx-mm-opt:hover { background:${L.paper2} !important; }
    .lx-mm-card { transition:border-color .3s ${L.ease}, background .3s ${L.ease}, box-shadow .3s ${L.ease}; }
    .lx-mm-card:hover { border-color:${L.ink} !important; }
    .lx-mm-drop { transition:border-color .3s ${L.ease}, background .3s ${L.ease}; }
    .lx-mm-drop:hover { border-color:${L.ink} !important; background:${L.card} !important; }
    .lx-mm .lx-btn:disabled:hover { background:${L.ink} !important; color:${L.paper} !important; }
    .lx-mm [role="checkbox"]:focus-visible, .lx-mm [role="radio"]:focus-visible, .lx-mm button:focus-visible { outline:1px solid ${L.gold}; outline-offset:3px; }
    .lx-mm-agree p { margin:0 0 12px; }
    @media (prefers-reduced-motion: reduce) { .lx-mm-next, .lx-mm-prev { animation:none !important; } }
  `;

  // Agreement heading styles (signature modal).
  const agH3 = { fontFamily: L.serif, fontSize: 21, fontWeight: 500, color: L.ink, margin: '22px 0 10px', paddingBottom: 8, borderBottom: `1px solid ${L.line}`, lineHeight: 1.25 };
  const agH4 = { fontFamily: L.serif, fontSize: 18, fontWeight: 600, color: L.ink, margin: '22px 0 8px', letterSpacing: '.01em' };
  const agH5 = { ...kicker({ fontSize: 12, color: L.goldDeep, letterSpacing: '.14em' }), margin: '16px 0 6px' };

  const why = whyOf(currentStepData);

  return (
    <div className="lx-root lx-mm" style={{
      minHeight: '100vh',
      background: L.paper,
      display: isDesktop ? 'grid' : 'block',
      gridTemplateColumns: isDesktop ? 'minmax(340px, 36%) minmax(0, 1fr)' : undefined,
      position: 'relative',
    }}>
      <LuxeStyles />
      <style>{pageCss}</style>

      {isDesktop ? (
        /* ── Desktop: sticky editorial panel ─────────────────────────── */
        <aside className="lx-noscroll" style={{
          position: 'sticky', top: 0, height: '100vh', overflowY: 'auto', boxSizing: 'border-box',
          background: L.paper2, borderRight: `1px solid ${L.line}`,
          padding: '56px 56px 44px', display: 'flex', flexDirection: 'column',
        }}>
          <div className="lx-fade-up">{lockup(50)}</div>
          <div className="lx-fade-up lx-d1" style={kicker({ color: L.goldDeep, marginTop: 18, fontSize: 12 })}>
            Partner Programme · 红娘合伙人招募
          </div>
          <div className="lx-rule lx-rule-draw" style={{ margin: '30px 0 26px', background: L.ink, width: 48 }} />
          <div className="lx-fade-up lx-d2" style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 30, lineHeight: 1.15, color: L.ink }}>
            {tr('红娘合伙人申请', 'Matchmaker application')}
          </div>
          <div className="lx-fade-up lx-d2" style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 17, color: MUTE, marginTop: 6 }}>
            {tr('Matchmaker application', '红娘合伙人申请')}
          </div>
          <div className="lx-fade-up lx-d2" style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Briefcase size={14} strokeWidth={1.25} color={L.goldDeep} />
            <span style={kicker({ fontSize: 12, color: L.ink2, letterSpacing: '.16em' })}>{typeLabel}</span>
          </div>
          <p className="lx-fade-up lx-d3" style={{ fontFamily: L.sans, fontSize: 14, fontWeight: 300, color: L.ink2, lineHeight: 1.7, margin: '22px 0 0', maxWidth: 340 }}>
            {tr(`只需 ${steps.length} 步，几分钟即可完成。每一步只问一件事，我们会告诉您为什么需要它。`,
                `Just ${steps.length} short steps — a few minutes in all. One question at a time, and we'll tell you why we ask.`)}
          </p>

          <div className="lx-fade-up lx-d3" style={{ marginTop: 36 }}>
            <div style={kicker({ fontSize: 12, marginBottom: 12 })}>{tr('目录', 'Contents')}</div>
            <ol style={{ listStyle: 'none', margin: 0, padding: 0, borderTop: `1px solid ${L.line}` }}>
              {steps.map((s, i) => {
                const current = i === currentStep;
                const done = i < currentStep;
                return (
                  <li key={s.id} aria-current={current ? 'step' : undefined} style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '8px 0', borderBottom: `1px solid ${L.lineSoft}`,
                    color: current ? L.ink : done ? L.ink2 : MUTE,
                    transition: `color .4s ${L.ease}`,
                  }}>
                    <span style={{
                      fontFamily: L.serif, fontSize: 16, width: 22, fontVariantNumeric: 'tabular-nums',
                      color: current ? L.ink : done ? L.ink2 : MUTE,
                    }}>{pad2(i + 1)}</span>
                    <span style={{ flex: 1, fontFamily: L.sans, fontSize: 13.5, letterSpacing: '.04em', fontWeight: current ? 500 : 300 }}>
                      {labelOf(s)}
                    </span>
                    {current && <span style={{ width: 22, height: 1, background: L.ink }} />}
                    {done && <Check size={13} strokeWidth={1.25} color={L.gold} />}
                  </li>
                );
              })}
            </ol>
          </div>

          <div style={{ marginTop: 'auto', paddingTop: 32 }}>
            <div style={{ height: 1, background: L.line, position: 'relative', overflow: 'hidden' }}>
              <div style={{
                position: 'absolute', left: 0, top: 0, bottom: 0, width: `${progress}%`,
                background: L.goldDeep, transition: `width .7s ${L.easeOut}`,
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <span style={kicker({ fontSize: 12 })}>{tr('进度', 'Progress')}</span>
              <span style={kicker({ fontSize: 12, color: L.ink })}>{stepCounter}</span>
            </div>
          </div>
        </aside>
      ) : (
        /* ── Mobile: masthead + thin progress line ───────────────────── */
        <header style={{ padding: '20px 16px 0' }}>
          <div className="lx-fade" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            {lockup(26)}
            {langToggle}
          </div>
          <div className="lx-fade lx-d1" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
            <span style={kicker({ fontSize: 12, color: L.goldDeep })}>{tr('红娘合伙人申请', 'Matchmaker application')}</span>
            {loginLink}
          </div>
          <div style={{ height: 1, background: L.line, position: 'relative', overflow: 'hidden', marginTop: 18 }}>
            <div style={{
              position: 'absolute', left: 0, top: 0, bottom: 0, width: `${progress}%`,
              background: L.ink, transition: `width .7s ${L.easeOut}`,
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, gap: 12 }}>
            <span style={kicker({ fontSize: 12, color: L.ink })}>{tr('步骤', 'Step')} {stepCounter}</span>
            <span style={kicker({ fontSize: 12 })}>{labelOf(currentStepData)}</span>
          </div>
        </header>
      )}

      {/* ── Form column ─────────────────────────────────────────────────── */}
      <main style={{ display: 'flex', flexDirection: 'column', minHeight: isDesktop ? '100vh' : undefined, minWidth: 0 }}>
        {isDesktop && (
          <div className="lx-fade" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 28, padding: '30px 56px 0' }}>
            {langToggle}
            {loginLink}
          </div>
        )}

        <div style={{
          flex: 1, display: 'flex', alignItems: isDesktop ? 'center' : 'flex-start', justifyContent: 'center',
          padding: isDesktop ? '40px 56px 72px' : '32px 16px 48px',
        }}>
          {/* Question Card */}
          <div
            key={currentStep}
            className={direction === 'next' ? 'lx-mm-next' : 'lx-mm-prev'}
            style={{ width: '100%', maxWidth: isWideStep && isDesktop ? 720 : 560 }}
          >
            {/* Numbered kicker */}
            <div className="lx-fade lx-d1" style={{
              display: 'flex', alignItems: 'center', gap: 16,
              borderBottom: `1px solid ${L.ink}`, paddingBottom: 14,
            }}>
              <span style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 20, color: L.goldDeep, whiteSpace: 'nowrap' }}>No. {pad2(currentStep + 1)}</span>
              <span style={kicker({ fontSize: 12, letterSpacing: '.26em', minWidth: 0 })}>{labelOf(currentStepData)}</span>
              {StepIcon && <StepIcon size={14} strokeWidth={1.25} color={MUTE} style={{ marginLeft: 'auto' }} />}
            </div>

            {/* Question */}
            <h1 className="lx-fade-up lx-d1" style={display(isDesktop ? 52 : 34, { marginTop: 22 })}>
              {currentStepData.question}
            </h1>

            <p className="lx-fade-up lx-d2" style={{
              fontFamily: L.serif, fontStyle: 'italic', fontSize: isDesktop ? 20 : 18,
              color: L.ink2, margin: '12px 0 0', lineHeight: 1.4,
            }}>
              {currentStepData.subtitle}
            </p>

            {/* Why we ask */}
            {why && (
              <div className="lx-fade-up lx-d2" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14 }}>
                <span style={{ width: 14, height: 1, background: L.gold, flexShrink: 0, marginTop: 10 }} />
                <span style={{ fontFamily: L.sans, fontSize: 13.5, fontWeight: 300, color: MUTE, lineHeight: 1.6 }}>
                  <span style={{ color: L.goldDeep, fontWeight: 400 }}>{tr('为什么需要：', 'Why we ask: ')}</span>
                  {why}
                </span>
              </div>
            )}

            <div style={{ height: isDesktop ? 34 : 26 }} />

            {/* Input Field */}
            <div className="lx-fade-up lx-d3">
              {!['checkbox', 'location', 'companyContact', 'planSelection', 'payment', 'ssn', 'w9Upload'].includes(currentStepData.type) && fieldKicker(labelOf(currentStepData))}
              {renderInputField()}
            </div>

            {/* Action Buttons */}
            <div className="lx-fade-up lx-d4" style={{
              marginTop: isDesktop ? 44 : 36, display: 'flex', gap: 12,
              justifyContent: currentStep > 0 ? 'space-between' : 'flex-end',
            }}>
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={handlePrev}
                  className="lx-btn-ghost"
                  aria-label={tr('上一步', 'Back')}
                  style={{
                    ...btn.ghost, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    padding: isDesktop ? '0 24px' : '0 18px', minHeight: 44, fontSize: 12.5, letterSpacing: '.22em', flex: '0 0 auto',
                  }}
                >
                  <ArrowLeft size={15} strokeWidth={1.5} />
                  {tr('上一步', 'Back')}
                </button>
              )}

              {memberConfirmOpen ? null : !isLast ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="lx-btn"
                  style={{
                    ...btn.primary, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                    padding: '0 30px', minHeight: 44, fontSize: 12.5, letterSpacing: '.22em', flex: isDesktop ? '0 0 auto' : 1,
                  }}
                >
                  {emailCheck.loading && currentStepData.id === 'email' ? tr('检查中…', 'Checking…') : tr('继续', 'Continue')}
                  <ArrowRight size={15} strokeWidth={1.5} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="lx-btn"
                  style={{
                    ...btn.primary, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                    padding: '0 30px', minHeight: 44, fontSize: 12.5, letterSpacing: '.22em', flex: isDesktop ? '0 0 auto' : 1,
                    opacity: loading ? 0.55 : 1, cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? tr('正在创建账号…', 'Creating Account...') : (
                    <>
                      {tr('创建账号', 'Create Account')}
                      <Check size={15} strokeWidth={1.5} />
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Keyboard Hint */}
            {isDesktop && !['checkbox', 'location', 'companyContact', 'planSelection', 'payment'].includes(currentStepData.type) && !memberConfirmOpen && (
              <div className="lx-fade lx-d5" style={{
                marginTop: 26, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
                ...kicker({ fontSize: 12, letterSpacing: '.14em' }),
              }}>
                {tr('按 ', 'Press ')}<kbd style={{
                  border: `1px solid ${L.line}`, borderRadius: L.radius, padding: '2px 8px',
                  fontFamily: L.sans, fontSize: 13, color: L.ink, background: L.card, letterSpacing: '.06em',
                }}>Enter ↵</kbd>{tr(' 继续', ' to continue')}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Signature Modal — clicking the terms checkbox opens this. Portalled
          to <body> so no animated ancestor can trap the fixed overlay. */}
      {showSignatureModal && createPortal(
        <div className="lx-root lx-mm" role="dialog" aria-modal="true" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(17,17,17,.55)', display: 'flex',
          alignItems: isDesktop ? 'center' : 'flex-end', justifyContent: 'center', zIndex: 1000,
          padding: isDesktop ? 24 : 0, backdropFilter: 'blur(3px)',
        }}>
          <div className="lx-fade-up" style={{
            background: L.card, borderRadius: L.radius,
            width: '100%', maxWidth: 600,
            maxHeight: isDesktop ? '90vh' : '94vh', display: 'flex', flexDirection: 'column',
            boxShadow: 'none', border: `1px solid ${L.line}`, overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: isDesktop ? '28px 32px 20px' : '20px 16px 14px', borderBottom: `1px solid ${L.line}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexShrink: 0,
            }}>
              <div>
                <div style={kicker({ fontSize: 12, color: L.goldDeep, marginBottom: 10 })}>
                  {tr('合伙人协议', 'Partner Agreement')} · FINDU
                </div>
                <h2 style={display(isDesktop ? 32 : 26)}>
                  {tr('签署合作协议', 'Sign Partner Agreement')}
                </h2>
                <p style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 16, color: MUTE, margin: '6px 0 0' }}>
                  {tr('Sign Partner Agreement', '签署合作协议')}
                </p>
              </div>
              <button
                type="button"
                aria-label={tr('关闭', 'Close')}
                onClick={() => setShowSignatureModal(false)}
                className="lx-icon-btn"
                style={{ ...btn.icon, width: 44, height: 44, flexShrink: 0 }}
              ><X size={16} strokeWidth={1.25} /></button>
            </div>

            {/* Body */}
            <div style={{ padding: isDesktop ? '24px 32px' : '18px 16px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
              <p style={{ fontFamily: L.sans, fontSize: 13.5, fontWeight: 300, color: L.ink2, lineHeight: 1.6, margin: '0 0 14px' }}>
                {tr('请向下滑动阅读协议全文，然后点击底部「签署并同意」。', 'Scroll to read the full agreement, then tap "Sign & Agree" at the bottom.')}
              </p>
              {/* Agreement Text */}
              <div className="lx-mm-agree" style={{
                background: L.paper, border: `1px solid ${L.line}`, borderRadius: L.radius,
                padding: isDesktop ? '20px 22px' : 16, marginBottom: 20,
                fontFamily: L.sans, fontSize: 13.5, fontWeight: 400, color: L.ink2, lineHeight: 1.75,
                maxHeight: isDesktop ? 320 : '42vh', overflowY: 'auto',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <FileText size={16} strokeWidth={1.25} color={L.goldDeep} />
                  <span style={{ fontFamily: L.serif, fontSize: 21, fontWeight: 500, color: L.ink, lineHeight: 1.2 }}>{partnerAgreement?.enabled && partnerAgreement?.title ? partnerAgreement.title : 'Partner Agreement / 合作协议'}</span>
                </div>
                {partnerAgreement?.enabled && partnerAgreement?.content ? (
                  <div dangerouslySetInnerHTML={{ __html: partnerAgreement.content.replace(/\n/g, '<br/>') }} />
                ) : (
                <>
                <p>By signing below, I agree to the Terms of Service and Privacy Policy of the matchmaking platform. I acknowledge that I have read, understand, and agree to be bound by all terms and conditions set forth in the Partner Agreement. This electronic signature has the same legal effect as a handwritten signature:</p>

                <h3 style={agH3}>Agreement with Matchmakers<br/><span style={{ fontWeight: 400 }}>红娘协议</span></h3>
                <p>This Agreement with Matchmakers ("Agreement") is made between FINDU, a professional matchmaking agency in Los Angeles ("Company"), and ("Matchmakers"). The Company offers matchmaking services through both online and offline channels. The matchmaker's obligation in a matchmaking company is to facilitate introductions and connections between clients in accordance with company policies and standards.</p>
                <p>本服务协议 " 协议 " 由洛杉矶 FINDU 专业婚恋服务机构 " 公司 " 与 "红娘"签订。公司通过线上和线下渠道提供婚恋服务。红娘在婚恋介绍公司中的义务是根据公司政策和标准促进客户之间的介绍和联系。</p>

                <h4 style={agH4}>COMMISSION SYSTEM AND LEVELS OF MATCHMAKERS</h4>
                <p>The commission split is differentiated based on the matchmaker's level<br/>佣金分成根据红娘的不同等级划分</p>

                <h5 style={agH5}>Ordinary Partners</h5>
                <p><strong>Conditions (meets one of the two):</strong><br/>前提条件两者满足其中之一即可</p>
                <ul style={{ paddingLeft: 20, margin: '6px 0' }}>
                  <li>Payment: Pay $2000 agency fee to the company to become an individual partner. 缴费给公司交$2000机构费成为个人合伙人。</li>
                  <li>No payment: 40% of each transaction is accumulated to 1800 points (points are US dollars) in one year, and you can become an individual partner. 不缴费累计每笔交易的 40%总计一年内达到 1800 积分积分即为美金即可成为个人合伙人。</li>
                </ul>
                <p><strong>Commission after reaching individual partner:</strong><br/>Before becoming an individual partner, you will receive a 40% performance commission for each order.<br/>Commission after reaching individual partner: Get 50% service fee commission for each order.</p>
                <p>未达到个人合伙人之前每单获得 40%业绩提成。达到个人合伙人后的提成：获得个人合伙人介绍的每单 50%服务费提成。</p>
                <p><em>Note: Renewal from the second year onwards requires 3 new members from the previous year if the individual partner is to be maintained.</em><br/>第二年起如果需维持个人合伙人续签需要前年新增3个会员。</p>

                <h5 style={agH5}>Senior Partners 资深合伙人</h5>
                <p><strong>Requirements:</strong> You need to recommend more than 3 individual or institutional partner. Also team performance of 2w points.<br/>强制条件需推荐三个以上个人合伙人。同时团队业绩达到2万积分。</p>
                <p><strong>Commission:</strong> Receive 60% service fee commission for each order. Qualified to recruit and train partners. If an individual partner recruits a lower-level individual partner, he/she can enjoy the additional 10% bonus for each order directly developed by the next level individual partner.<br/>获得每单 60%服务费提成。有资格招募并培训合伙人。个人合伙人如果招募下一级个人合伙人可享受公司额外给与下一级个人合伙人直接发展会员每单 10%的额外奖励。</p>

                <h5 style={agH5}>Institutional Partner 机构合伙人</h5>
                <p><strong>Meet the conditions:</strong> Individual Partners can upgrade to Institutional Partners after accumulating 3 new Senior Partners. Also team performance of 8w points.<br/>个人合伙人两代累积新增达到3个资深合伙人可升级机构合伙人。同时团队业绩达到8w积分。</p>
                <p><strong>Commission:</strong> Receive 70% of the commission of the directly developed members.<br/>可获得直接发展会员 70%的提成。</p>
                <p>For its development of individual general partner team and senior partner team, according to 70% of the total amount of each single commission, after deducting all levels of individual partner commission, you can get the remaining part.<br/>对于其发展的个人普通合伙人团队以及资深合伙人团队按照 70%计算每单的提成总额扣除各级个人合伙人提成后可获得剩余部分。</p>
                <p>For the institutional partners developed by the company, the company additionally rewards the performance of the first tier of institutional partners developed with an additional bonus of 8%, the second tier with 2%, and the third tier with 1%.<br/>对于其发展的机构合伙人公司额外奖励被发展第一层机构合伙人业绩 8%的额外奖励第二层2%第三层1%。</p>

                <h4 style={agH4}>Benefits of Partner Matchmakers 红娘合伙人权益</h4>
                <p>1. The Partner Matchmakers shall have access to shared client resources provided by the Company.<br/>红娘合伙人可获得公司提供的共享客户资源。</p>
                <p>2. The Partner Matchmakers shall have the right to utilize physical venues provided by the Company for conducting matchmaking activities.<br/>红娘合伙人有权使用公司提供的实体场地以进行婚恋活动。</p>
                <p>3. The Partner Matchmakers shall be permitted to use the internal system provided by the Company.<br/>红娘合伙人可使用公司提供的内部互联网系统。</p>

                <h4 style={agH4}>REQUIREMENTS 对红娘的要求</h4>
                <ol style={{ paddingLeft: 20, margin: '6px 0' }}>
                  <li style={{ marginBottom: 8 }}>Matchmakers shall not engage in, or facilitate, fraudulent marriages.<br/>红娘不得参与或协助假结婚。</li>
                  <li style={{ marginBottom: 8 }}>Matchmakers shall not disseminate false or misleading information.<br/>红娘不得传播虚假或误导性信息。</li>
                  <li style={{ marginBottom: 8 }}>Matchmakers shall maintain the confidentiality of all internal company materials and client privacy information.<br/>红娘应维护所有公司内部资料和客户隐私信息的机密性。</li>
                  <li style={{ marginBottom: 8 }}>Matchmakers shall independently and strictly verify client information.<br/>红娘应独立且严格审核客户提供的信息。</li>
                  <li style={{ marginBottom: 8 }}>All personal illegal actions by matchmakers shall be deemed independent of the Company.<br/>所有红娘的个人违法行为均被视为与公司无关。</li>
                  <li style={{ marginBottom: 8 }}>Matchmakers shall not accept private orders or open another organization of the same type.<br/>红娘不得接私单。不得自己再开同类型机构。</li>
                  <li style={{ marginBottom: 8 }}>No cross-border marriage between China and the United States.<br/>不得做中美跨国婚恋。</li>
                  <li style={{ marginBottom: 8 }}>Partners are required to upload all members' information to the company.<br/>合伙人必须把所有会员的信息都上传到公司并认证。</li>
                  <li style={{ marginBottom: 8 }}>The company requires partners not to disclose commissions and business secrets.<br/>公司给合伙人的提成以及公司其他商业机密要求合伙人不能在外泄露。</li>
                  <li style={{ marginBottom: 8 }}>Each member must verify their real name and meet the person in person.<br/>每个会员必须实名认证见到本人。</li>
                  <li style={{ marginBottom: 8 }}>Not to malign the company's reputation in any form.<br/>不得以各种形式恶意诋毁公司声誉以及同行竞争。</li>
                  <li style={{ marginBottom: 8 }}>If leaving, not allowed to work in the same industry for 5 years.<br/>公司或机构的相关人员如果离职5年内不得做同行业。</li>
                </ol>

                <h4 style={agH4}>MISCELLANEOUS 其他条款</h4>
                <p><strong>Modification:</strong> This Agreement may be modified by subsequent agreement of the parties only by an instrument in writing signed by both of them.<br/>本协议只能通过双方签署的书面文件进行修改。</p>
                <p><strong>Applicable Law:</strong> This Agreement shall be governed by the laws of the State of California. Venue for any action shall be in Los Angeles County, California.<br/>本协议受加利福尼亚州法律管辖。任何执行本协议条款的诉讼地点均为加利福尼亚州洛杉矶县。</p>
                <p><strong>Language:</strong> This Agreement is written and executed in English and Chinese. The English version shall prevail.<br/>本协议以英文和中文书写和执行。</p>
                <p><strong>Attorney's Fees:</strong> All damages, including attorney's fees, will be paid by the losing party.<br/>所有赔偿包括律师费等将由败诉方支付。</p>
                <p><strong>Tax Treatment:</strong> According to California law, partners are required to declare and pay taxes on all income. The company will provide 1099-MISC forms every year.<br/>根据加州法律合伙人需自行申报并缴纳其在合作期间获得的所有收入的税款。公司将在每年提供 1099-MISC 表格供合伙人用于报税。</p>
                <p><strong>Settlement:</strong> Monthly settlement before the 15th of each working day. All payable commissions paid within 5 working days after settlement.<br/>公司将每月工作日 15 日前进行提成和收益的清算。所有应付提成和收益将在清算后的 5 个工作日内支付。</p>

                <div style={{ marginTop: 20, padding: '16px 18px', background: L.card, borderRadius: L.radius, border: `1px solid ${L.line}` }}>
                  <p style={{ margin: 0, fontWeight: 600, color: L.ink }}>IN WITNESS WHEREOF, the parties hereto have executed this Agreement as of the date first above written.</p>
                  <p style={{ margin: '6px 0 0', color: L.ink2 }}>兹此签署本协议各方已于上述日期签署本协议。</p>
                  <p style={{ margin: '10px 0 0', fontWeight: 700, color: L.goldDeep }}>FINDU<br/>洛杉矶 FINDU 婚恋服务专业机构</p>
                </div>
                </>
                )}
              </div>

              {/* Signature date */}
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 14, paddingBottom: 12, borderBottom: `1px solid ${L.line}` }}>
                <label style={kicker({ fontSize: 12, whiteSpace: 'nowrap' })}>{tr('签署日期：', 'Signature Date:')}</label>
                <span style={{ fontFamily: L.serif, fontSize: 18, color: L.ink }}>
                  {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </div>

              {noteBox(language === 'zh'
                ? '点击下方「签署并同意」，即表示您确认并同意签署合作伙伴协议。'
                : 'Clicking "Sign & Agree" below constitutes your confirmation and agreement to sign the Partner Agreement.',
                { marginBottom: 18 })}

              {/* Signature preview — fixed handwriting style, no picker */}
              <div>
                <div style={kicker({ fontSize: 12, color: L.ink, marginBottom: 8 })}>{tr('您的签名', 'Your signature')}</div>
                <div style={{
                  padding: '12px 4px 10px', borderBottom: `1px solid ${L.ink}`,
                }}>
                  <div style={{ fontFamily: SIGNATURE_FONT, fontSize: 32, color: L.ink, lineHeight: 1.2, wordBreak: 'break-word' }}>
                    {`${formData.firstName} ${formData.lastName}`.trim() || tr('您的姓名', 'Your Name')}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: isDesktop ? '18px 32px 26px' : '14px 16px calc(16px + env(safe-area-inset-bottom, 0px))',
              borderTop: `1px solid ${L.line}`, background: L.card, flexShrink: 0,
              display: 'flex', gap: 12, justifyContent: 'flex-end',
            }}>
              <button
                type="button"
                onClick={() => setShowSignatureModal(false)}
                className="lx-btn-ghost"
                style={{ ...btn.ghost, padding: '0 22px', minHeight: 44, fontSize: 12.5, letterSpacing: '.22em', flex: isDesktop ? '0 0 auto' : '0 0 auto' }}
              >
                {tr('取消', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!signatureStyle) return;
                  const fullName = `${formData.firstName} ${formData.lastName}`.trim();
                  const data = {
                    name: fullName,
                    fontFamily: signatureStyle,
                    signedAt: new Date().toISOString()
                  };
                  setSignatureData(data);
                  setFormData({ ...formData, agreedToTerms: true });
                  setShowSignatureModal(false);
                  if (errors.agreedToTerms) {
                    setErrors({ ...errors, agreedToTerms: null });
                  }
                }}
                disabled={!signatureStyle}
                className="lx-btn"
                style={{
                  ...btn.primary, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  padding: '0 22px', minHeight: 44, fontSize: 12.5, letterSpacing: '.22em', flex: isDesktop ? '0 0 auto' : 1,
                  opacity: signatureStyle ? 1 : 0.4, cursor: signatureStyle ? 'pointer' : 'not-allowed',
                }}
              >
                <Shield size={15} strokeWidth={1.5} />
                {tr('签署并同意', 'Sign & Agree')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MatchmakerRegister;
